import { FC, useEffect, useLayoutEffect, useState } from 'react'
import { Outlet, useOutletContext, useRevalidator } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'

// ROUTES
import StatusRoute from './status'
import WelcomeRoute from './welcome'
import ContributeRoute from './contribute'
import PledgeRoute from './pledge'
import CardRoute from './card'
import CardDetailsRoute from './card-details'
import EditRoute from './edit'
import CardReadyRoute from './card-ready'
import ConfirmationRoute from './confirmation'
import InviteRoute from './invite'
import PaymentInfoRoute from './payment-info'
import AddRoute from './add'

// TYPES
import type { RouteObject } from 'react-router-dom'
import type { PaymentIntent, SetupIntent } from '@stripe/stripe-js'

// UTILITIES
import { parsePledge } from '@schemas'
import { IoTClient } from '../../../_mqtt/config'
import { isObject } from '@utils/type-checkers'
import { groupRouteId, loader, useGroupLoaderData } from './_hooks'
import { isDemoGroup } from './demo-data'
import { queryClient } from '@/lib/queryClient'

export { useGroupLoaderData }




interface PledgeContextState {
    setupIntent?: SetupIntent
    paymentIntent?: PaymentIntent
    pledge: Pledge
    lockedSlots: Record<string, LockingItem>
}
type PledgeContext = [PledgeContextState, React.Dispatch<React.SetStateAction<PledgeContextState>>]
export const usePledgeOutletContext = () => useOutletContext<PledgeContext>()

/** 10 minutes in milliseconds */
const maxClaimTime = 600000
const Layout: FC<{}> = () => {
    const group = useGroupLoaderData()
    const revalidator = useRevalidator()
    const context = useState<PledgeContextState>({
        pledge: parsePledge(),
        lockedSlots: createSlotsFromGroup(group.pledges || [])
    })
    const [state, setState] = context
    const lockedSlots = state.lockedSlots

    const { isLoaded, isSignedIn, user } = useUser()

    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible')
            revalidator.revalidate()
    }

    useLayoutEffect(() => {
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    })

    useEffect(() => {
        const callback = (group: Group) => {
            console.log('Group Update Recieved:', group)
            revalidator.revalidate()
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })
        }

        if (isDemoGroup(group.id)) return
        if (!!user?.id && isLoaded && isSignedIn) {
            const iotClient = IoTClient.getInstance(user.id)
            iotClient.addTopicListener('/group/*/', callback, { wildcard: group.id })
            return () => {
                iotClient.removeTopicListener('/group/*/', callback, { wildcard: group.id })
            }
        }
    }, [group.id, user?.id, isLoaded, isSignedIn])

    // This useEffect handles the connecting and processing events from the locking
    // channel for the particular group the user is viewing.
    useEffect(() => {
        const callback = (lockingState: Record<string, LockingItem>) => {
            console.log("Slot Locking Event Recieved:", lockingState)
            if (isObject(lockingState)) {
                const lockedSlots = validageSlots(group.pledges || [], lockingState)
                setState(({ pledge, ...rest }) => ({
                    ...rest,
                    pledge: parsePledge(lockedSlots[`${pledge.id}`]?.status === 'open' ? {} : pledge),
                    lockedSlots,
                }))
            }
        }

        if (isDemoGroup(group.id)) return
        if (!!user?.id && isLoaded && isSignedIn) {
            const iotClient = IoTClient.getInstance(user.id)
            iotClient.addTopicListener('/group/*/locking/', callback, { wildcard: group.id })
            return () => {
                iotClient.removeTopicListener('/group/*/locking/', callback, { wildcard: group.id })
            }
        }
    }, [group.id, user?.id, isLoaded, isSignedIn])

    useEffect(() => {
        setState((currentState) => ({
            ...currentState,
            lockedSlots: validageSlots(group.pledges || [], currentState.lockedSlots),
        }))
    }, [group.pledges])

    useEffect(() => {
        const timestamp = new Date().getTime()
        const timeouts = Object.keys(lockedSlots).reduce<NodeJS.Timeout[]>((prev, pledgeId) => {
            const item = lockedSlots[pledgeId]
            if (item.status === 'claimed') {
                const timeRemaining = Math.max(maxClaimTime - (timestamp - item.updatedAt), 0)
                if (!!timeRemaining) return [...prev, setTimeout(unlockSlot, timeRemaining, pledgeId)]
            }
            return prev
        }, [])
        return () => { timeouts.forEach((timeout) => clearTimeout(timeout)) }
    }, [lockedSlots])

    function unlockSlot(pledgeId: string) {
        if (isDemoGroup(group.id)) return
        const newState = { ...lockedSlots }
        newState[pledgeId] = { status: 'open', updatedAt: new Date().getTime() }

        IoTClient.getInstance().publishMessage(`/group/${group.id}/locking/`, {
            Event: 'MODIFY',
            Type: 'locking',
            Item: newState
        } satisfies LockingEvent, { retain: true })
    }

    return (<>
        <Outlet context={context} />
    </>)
}

const createSlotsFromGroup = (pledges: Pledge[]): Record<string, LockingItem> => pledges.reduce<Record<string, LockingItem>>((prev, pledge) => ({ ...prev, [`${pledge.id}`]: createLockingItem(pledge) }), {})
const validageSlots = (pledges: Pledge[], lockingState: Record<string, LockingItem>) => pledges.reduce<Record<string, LockingItem>>((prev, pledge) => {
    const id = `${pledge.id}`
    const state: Record<string, LockingItem> = { ...prev }
    const item = (!!pledge.transactionId || !!pledge.user?.id) ? createLockingItem(pledge) : lockingState[id]

    if (!item) state[id] = createLockingItem(pledge);
    else switch (item.status) {
        case 'open':
            state[id] = item
            break
        case 'claimed':
            const timestamp = new Date().getTime()
            const timeRemaining = Math.max(maxClaimTime - (timestamp - item.updatedAt), 0)
            state[id] = !!timeRemaining ? item : createLockingItem(pledge)
            break
        case 'settling':
            state[id] = item
            break
        case 'purchased':
            state[id] = (!!pledge.transactionId || !!pledge.user?.id) ? item : createLockingItem(pledge)
            break
        default: break
    }

    return state
}, {})
const createLockingItem = (pledge: Pledge): LockingItem => {
    const timestamp = new Date().getTime()
    if (!!pledge.transactionId || !!pledge.user?.id) return { status: 'purchased', updatedAt: timestamp, user: pledge.user || {} }
    else return { status: 'open', updatedAt: timestamp }
}


const GroupIdRoute: RouteObject = {
    id: groupRouteId,
    path: ':groupId',
    element: <Layout />,
    loader,
    children: [
        StatusRoute,
        WelcomeRoute,
        ContributeRoute,
        PledgeRoute,
        CardRoute,
        CardDetailsRoute,
        EditRoute,
        CardReadyRoute,
        ConfirmationRoute,
        InviteRoute,
        PaymentInfoRoute,
        AddRoute
    ]
}

export default GroupIdRoute
